import { useState } from "react";
import { Link } from "react-router-dom";
import { httpsCallable } from "firebase/functions";
import {
  CALLABLE_FUNCTIONS,
  type CreateEnrollmentTokenRequest,
  type CreateEnrollmentTokenResponse,
  type RevokeEnrollmentTokenRequest,
  type RevokeEnrollmentTokenResponse,
} from "@srd/shared";
import { useAuth } from "../auth/AuthProvider";
import { functions } from "../firebase";

export function EnrollmentPage() {
  const { claims, signOutUser } = useAuth();
  const [label, setLabel] = useState("");
  const [groups, setGroups] = useState("");
  const [platform, setPlatform] = useState<"windows" | "">("windows");
  const [ttlHours, setTtlHours] = useState(24);
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [enrollCmd, setEnrollCmd] = useState<string | null>(null);
  const [tokenId, setTokenId] = useState("");
  const isAdmin = claims?.role === "admin";

  async function createToken() {
    setError(null);
    setStatus("Creating enrollment token…");
    setEnrollCmd(null);
    try {
      const callable = httpsCallable<
        CreateEnrollmentTokenRequest,
        CreateEnrollmentTokenResponse
      >(functions, CALLABLE_FUNCTIONS.createEnrollmentToken);
      const result = await callable({
        label: label || undefined,
        groups: groups
          ? groups.split(",").map((g) => g.trim()).filter(Boolean)
          : undefined,
        platform: platform || undefined,
        ttlHours,
      });
      const data = result.data;
      setTokenId(data.tokenId);
      setEnrollCmd(
        `srd-agent enroll --token-id ${data.tokenId} --secret ${data.enrollmentSecret}`,
      );
      setStatus(`tokenId=${data.tokenId}\nexpiresAt=${data.expiresAt}`);
    } catch (err) {
      setError(String(err));
      setStatus(null);
    }
  }

  async function revokeToken() {
    setError(null);
    setStatus("Revoking token…");
    try {
      const callable = httpsCallable<
        RevokeEnrollmentTokenRequest,
        RevokeEnrollmentTokenResponse
      >(functions, CALLABLE_FUNCTIONS.revokeEnrollmentToken);
      const result = await callable({ tokenId, reason: "admin_console" });
      setStatus(`tokenId=${result.data.tokenId} status=${result.data.status}`);
      setEnrollCmd(null);
    } catch (err) {
      setError(String(err));
    }
  }

  return (
    <div className="page">
      <header className="topbar">
        <div>
          <h1>Enrollment tokens</h1>
          <p className="muted">
            One-time secrets for Windows agent enrollment. Plaintext shown once — never stored.
          </p>
        </div>
        <div className="row">
          <Link to="/devices">Devices</Link>
          <button type="button" onClick={() => void signOutUser()}>
            Sign out
          </button>
        </div>
      </header>

      {!isAdmin ? (
        <section className="panel">
          <p className="status err">Admin role required to create or revoke enrollment tokens.</p>
        </section>
      ) : (
        <>
          <section className="panel">
            <h2>Create token</h2>
            <div className="row">
              <input
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="label (optional)"
              />
              <input
                value={groups}
                onChange={(e) => setGroups(e.target.value)}
                placeholder="groups (comma-separated)"
              />
              <select
                value={platform}
                onChange={(e) => setPlatform(e.target.value as "windows" | "")}
              >
                <option value="windows">windows</option>
                <option value="">any platform</option>
              </select>
              <input
                type="number"
                min={1}
                max={72}
                value={ttlHours}
                onChange={(e) => setTtlHours(Number(e.target.value))}
                title="TTL hours"
                style={{ minWidth: "6rem" }}
              />
              <button type="button" className="primary" onClick={() => void createToken()}>
                Create token
              </button>
            </div>
            {enrollCmd ? (
              <div className="secret-box">
                <code>{enrollCmd}</code>
                <button
                  type="button"
                  onClick={() => void navigator.clipboard.writeText(enrollCmd)}
                >
                  Copy enroll command
                </button>
              </div>
            ) : null}
          </section>

          <section className="panel">
            <h2>Revoke unused token</h2>
            <div className="row">
              <input
                value={tokenId}
                onChange={(e) => setTokenId(e.target.value)}
                placeholder="tokenId"
              />
              <button type="button" onClick={() => void revokeToken()}>
                Revoke token
              </button>
            </div>
          </section>
        </>
      )}

      {status ? <p className="status ok">{status}</p> : null}
      {error ? <p className="status err">{error}</p> : null}
    </div>
  );
}
